import { useState, useEffect } from 'react';
import { Box, Button, Stack, Typography, Alert } from '@mui/material';
import Input from '../Input';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

function EUDRCredentialsForm({ onNotification }) {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    authKey: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState({ success: false, error: null });
  const [errors, setErrors] = useState({});

  // Load user credentials once on mount
  useEffect(() => {
    if (user && user.eudrCredentials) {
      setFormData({
        username: user.eudrCredentials.username || '',
        authKey: user.eudrCredentials.authKey || ''
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate username
    if (!formData.username) {
      newErrors.username = 'EUDR Username is required';
      isValid = false;
    }

    // Validate authKey
    if (!formData.authKey) {
      newErrors.authKey = 'Authentication Key is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      onNotification(
        'Validation Error',
        'Please check all required fields and try again.',
        'error'
      );
      return;
    }

    setLoading(true);
    setStatus({ success: false, error: null });

    try {
      await axios.post('/api/auth/eudr-credentials', formData);
      onNotification(
        'EUDR Credentials Updated',
        'Your EUDR credentials have been successfully saved.',
        'success'
      );
      setStatus({ success: true, error: null });
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to update EUDR credentials';
      setStatus({ success: false, error: errorMsg });
      onNotification(
        'Failed to Update EUDR Credentials',
        errorMsg,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      onNotification(
        'Validation Error',
        'Please check all required fields and try again.',
        'error'
      );
      return;
    }

    setTesting(true);
    setStatus({ success: false, error: null });

    try {
      const response = await axios.post('/api/auth/eudr-credentials/test', formData);
      onNotification(
        'Connection Test Successful',
        'Successfully connected to EUDR with the provided credentials.',
        'info'
      );
      setStatus({ success: true, error: null });
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to test EUDR connection';
      setStatus({ success: false, error: errorMsg });
      onNotification(
        'Connection Test Failed',
        errorMsg,
        'error'
      );
    } finally {
      setTesting(false);
    }
  };

  // Clear success message after a delay
  useEffect(() => {
    let timer;
    if (!status.error) {
      timer = setTimeout(() => {
        setStatus(prev => ({ ...prev, success: false }));
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [status.error]);

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h6" gutterBottom>
        EUDR Credentials Configuration
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Configure your EUDR credentials to enable integration with the EUDR compliance system.
      </Typography>

      <Stack spacing={3}>
        <Input
          margin="normal"
          required
          fullWidth
          id="username"
          label="EUDR Username"
          name="username"
          value={formData.username}
          onChange={handleChange}
          placeholder="Enter your EUDR username"
          error={!!errors.username}
          helperText={errors.username}
        />

        <Input
          margin="normal"
          required
          fullWidth
          id="authKey"
          label="Web Service Access Authentication Key"
          name="authKey"
          value={formData.authKey}
          onChange={handleChange}
          placeholder="Enter your EUDR Web Service Access Authentication Key"
          error={!!errors.authKey}
          helperText={errors.authKey}
        />

        <Typography variant="body2" color="text.secondary">
          Note: The Web Client ID (test/production system selection) is now configured per API key. 
          You can manage these settings in the API Keys section.
        </Typography>
      </Stack>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleTestConnection}
          disabled={testing || loading}
          sx={{ mr: 'auto' }}
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </Button>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading || testing}
          
        >
          {loading ? 'Saving...' : 'Save Credentials'}
        </Button>
      </Box>
    </Box>
  );
}

export default EUDRCredentialsForm; 